import { ORGANIZATION_ID, WWW_ORIGIN } from "./constants"
import type { JsonLdNode } from "./core"

export interface OrganizationOptions {
  /** 默认为 https://www.metal.com，子域应用（如 news.metal.com）仍指向主站组织 */
  origin?: string
  /** 应用从多语言资源读取的组织描述，未传时回退英文 */
  description?: string
  /** 应用从多语言资源读取的专业领域列表，未传时回退英文 */
  knowsAbout?: string[]
  /** 认证说明页完整 URL；调用方可传入带语言路径的 URL */
  certificationUrl?: string
}

/** 全局 Organization 节点（全站唯一，建议在根布局输出一次）。 */
export function buildOrganizationJsonLd(options?: OrganizationOptions): JsonLdNode {
  const origin = options?.origin ?? WWW_ORIGIN
  const certificationUrl = options?.certificationUrl ?? `${origin}/compliance-center/compliance-statement`
  return {
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name: "Shanghai Metals Market (SMM)",
    legalName: "SMM Information & Technology Co., Ltd.",
    description:
      options?.description ??
      "SMM is a global provider of benchmark price assessments, market intelligence, data, and research across metals and energy transition materials.",
    url: origin,
    foundingDate: "1999",
    logo: {
      "@type": "ImageObject",
      url: "https://imgqn.smm.cn/static/image/metal/logo.svg",
    },
    sameAs: [
      "https://www.linkedin.com/company/shanghai-metal-market/",
      "https://www.facebook.com/smm.china",
      "https://www.youtube.com/@ShanghaiMetalsMarket",
      "https://x.com/CnMetals",
      "https://www.instagram.com/smm_shanghaimetalsmarket/",
    ],
    hasCertification: {
      "@type": "Certification",
      name: "IOSCO Principles for Financial Benchmarks Assurance Review",
      url: certificationUrl,
    },
    areaServed: {
      "@type": "Place",
      name: "Global",
    },
    knowsAbout: options?.knowsAbout ?? [
      "Base Metals (Copper, Aluminum, Lead, Zinc, Nickel, Tin)",
      "Ferrous Metals (Steel, Iron Ore, Ferroalloys)",
      "New Energy & Battery Materials (Lithium, Cobalt, Nickel Sulfate)",
      "Rare Earth Elements",
      "Minor Metals (Magnesium, Titanium, Tungsten, etc.)",
      "Precious Metals (Gold, Silver, Platinum, Palladium)",
      "Photovoltaic Materials (Polysilicon, Solar Cells)",
      "Scrap Metals & Recycling",
      "Benchmark Price Assessments & Commodity Pricing",
      "Market Intelligence & Industry Research",
      "Metal Indexes & Futures",
      "Exchange Rates & Power Markets",
    ],
  }
}

export interface WebSiteOptions {
  origin?: string
  /** 站点名称，默认主站名；子站可传 "Shanghai Metals Market (SMM) News" 等 */
  name?: string
  /** 站点搜索地址模板，须包含 {search_term_string} 占位符；不传则不输出 SearchAction */
  searchUrlTemplate?: string
}

/** 全局 WebSite 节点（与 Organization 一起，在根布局输出一次）。 */
export function buildWebSiteJsonLd(options?: WebSiteOptions): JsonLdNode {
  const origin = options?.origin ?? WWW_ORIGIN
  const node: JsonLdNode = {
    "@type": "WebSite",
    "@id": `${origin}/#website`,
    url: origin,
    name: options?.name ?? "Shanghai Metals Market (SMM)",
    publisher: { "@id": ORGANIZATION_ID },
  }
  const searchUrlTemplate =
    options && "searchUrlTemplate" in options
      ? options.searchUrlTemplate
      : origin === WWW_ORIGIN
        ? `${origin}/search?keyword={search_term_string}`
        : undefined
  if (searchUrlTemplate) {
    node.potentialAction = {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: searchUrlTemplate,
      },
      "query-input": "required name=search_term_string",
    }
  }
  return node
}
